import type {GigShellData} from './shell/shellTypes';

export interface PostRequirement {
  key: 'feed-posts' | 'reels' | 'stories';
  label: string;
  to: string;
  required: number;
  accepted: number; // raw count (may exceed required if over-delivered)
  doneCount: number; // accepted capped at required — the count that counts toward "done"
  remaining: number;
  done: boolean;
}

export interface CombinedTotals {
  required: number;
  done: number;
  needsAction: number;
}

export function buildPostRequirements(
  shell: GigShellData,
  gigId: string
): PostRequirement[] {
  const network = shell.socialNetworkName ?? 'Social';
  const {feedPosts, reels, stories} = shell.counts;

  const candidates: Array<{
    key: PostRequirement['key'];
    label: string;
    to: string;
    counts: {required: number; accepted: number};
  }> = [
    {
      key: 'feed-posts',
      label: `${network} Posts`,
      to: `/gigs/${gigId}/posts`,
      counts: feedPosts,
    },
    {
      key: 'reels',
      label: `${network} Reels`,
      to: `/gigs/${gigId}/reels`,
      counts: reels,
    },
    {
      key: 'stories',
      label: `${network} Story`,
      to: `/gigs/${gigId}/stories`,
      counts: stories,
    },
  ];

  return candidates
    .filter((c) => c.counts.required > 0)
    .map((c) => {
      const remaining = Math.max(0, c.counts.required - c.counts.accepted);
      return {
        key: c.key,
        label: c.label,
        to: c.to,
        required: c.counts.required,
        accepted: c.counts.accepted,
        doneCount: Math.min(c.counts.accepted, c.counts.required),
        remaining,
        done: c.counts.accepted >= c.counts.required,
      };
    });
}

export function combineDeliverableTotals(
  uploads: {
    totalRequired: number;
    totalApproved: number;
    needsActionCount: number;
  },
  posts: PostRequirement[]
): CombinedTotals {
  const postsRequired = posts.reduce((sum, p) => sum + p.required, 0);
  const postsDone = posts.reduce((sum, p) => sum + p.doneCount, 0);
  const postsNeedsAction = posts.reduce((sum, p) => sum + p.remaining, 0);

  return {
    required: uploads.totalRequired + postsRequired,
    done: uploads.totalApproved + postsDone,
    needsAction: uploads.needsActionCount + postsNeedsAction,
  };
}
